import { add, differenceInMilliseconds, endOfHour, isBefore, startOfMinute } from 'date-fns';
import { MINUTE } from 'time-constants';

import { TimeUtils } from './TimeUtils';

/**
 * Get current system time and compare to expiration time UTC coming from API response. Will set
 * refresh time to refresh at expirationTimeUtc.
 * If the expiration time is in the past, the API might have returned it incorrectly. In this case
 * the defaultInterval is returned, which defaults to 10 minutes.
 */
export const refetchIntervalFallback = 10 * MINUTE;
export const refetchIntervalByExpirationTime = ({
  expirationTimeUtc,
  setRefetchInterval,
  defaultInterval = 10 * 60 * 1000,
}) => {
  if (!expirationTimeUtc) {
    setRefetchInterval(refetchIntervalFallback);
    return;
  }
  const currentTime = Date.prototype.getCurrent();
  const expirationTime = TimeUtils.dateFromEpoch(expirationTimeUtc);

  const differenceBetweenCurrentAndExpirationTimeMs = differenceInMilliseconds(expirationTime, currentTime);

  if (differenceBetweenCurrentAndExpirationTimeMs > 0) {
    setRefetchInterval(differenceBetweenCurrentAndExpirationTimeMs);
  } else {
    setRefetchInterval(refetchIntervalFallback);
  }
};

/**
 * Get current system time and compare to end of current hour time. Will set the refresh interval
 * to trigger at the end of the current hour.
 */
export const refetchIntervalByEndOfHour = ({ setRefetchInterval }) => {
  const currentTime = Date.prototype.getCurrent();
  const currentTimeEndOfHour = endOfHour(currentTime);

  const differenceBetweenCurrentAndEndOfHourTimeMs = differenceInMilliseconds(currentTimeEndOfHour, currentTime);

  setRefetchInterval(differenceBetweenCurrentAndEndOfHourTimeMs);
};

/**
 * Get current system time and compare to 3:30pm and 3:30am time
 * If before 3:30pm, set refetch to 3:30pm
 * If after 3:30pm, set refetch to 3:30am of next day
 * Note that requirements specify 3am/3pm, but API is can be stale this, we were told it will be reliably refreshed on the half hour
 */
export const refetchIntervalAt3pAnd3a = ({ setRefetchInterval }) => {
  const currentTime = Date.prototype.getCurrent();

  const todayTime = Date.prototype.getCurrent();

  todayTime.setHours(15);
  todayTime.setMinutes(30);
  const threePTime = startOfMinute(todayTime);

  const tomorrowTime = add(Date.prototype.getCurrent(), { days: 1 });

  tomorrowTime.setHours(3);
  tomorrowTime.setMinutes(30);
  const threeATime = startOfMinute(tomorrowTime);

  const before3pTime = isBefore(currentTime, threePTime);

  if (before3pTime) {
    const differenceBetweenCurrentAnd3pTimeMs = differenceInMilliseconds(currentTime, threePTime);

    setRefetchInterval(differenceBetweenCurrentAnd3pTimeMs);
  } else {
    const differenceBetweenCurrentAnd3aTimeMs = differenceInMilliseconds(currentTime, threeATime);

    setRefetchInterval(differenceBetweenCurrentAnd3aTimeMs);
  }
};
